import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { compact } from 'lodash-es';
import { AccountingInvoiceSyncStatusFailureCodeEnum } from 'anchor-ts-infra/src/dto/accounting-invoice/model';
import { InvoiceDisplayStatusV2 } from 'anchor-scout-common/types';
import { Box, Divider } from 'seagull';
import { Intent } from '~/infra/access-control';
import { handleRequest } from '~/infra/utils';
import { getAccountingDetails } from '~/integrations/Accounting/accounting.utils';
import { invoiceModel, paymentMethodModel, relationshipModel } from '~/models';
import { InvoiceStatus } from '~/models/invoice';
import { InvoiceOptimisticUpdateType } from '~/providers/InvoiceChangesObservableProvider';
import { Accounting } from '~/types/accounting';
import { getAccountingInvoiceErrorMessage } from '~/utils/getAccountingInvoiceErrorMessage.utils';
export async function syncInvoiceAndClassifyStaleAction(param) {
    let { readLatestInvoice, setInvoice, onRefresh, isActionStillRequired, isTerminallyBlocked = ()=>false } = param;
    const { success, data } = await handleRequest(readLatestInvoice());
    if (!success) return 'unclassified';
    setInvoice(data);
    onRefresh === null || onRefresh === void 0 ? void 0 : onRefresh({});
    if (!isActionStillRequired(data)) return 'statusChanged';
    return isTerminallyBlocked(data) ? 'stillBlocked' : 'unclassified';
}
export const invoiceSearchParam = 'invoice';
export const relationshipSearchParam = 'relationship';
export const utilsTextsPath = 'invoice.fullPageInvoice.utils';
export function getFooter(relationship, invoice, texts) {
    var _invoice_paymentMethodDisplay;
    const { paymentMethod, feeControls } = relationship;
    const isAdHoc = relationshipModel.isAdHoc(relationship);
    const effectivePaymentMethod = (_invoice_paymentMethodDisplay = invoice.paymentMethodDisplay) !== null && _invoice_paymentMethodDisplay !== void 0 ? _invoice_paymentMethodDisplay : paymentMethod;
    const coversCreditCardFees = isAdHoc ? invoice.coverCreditCardFees : feeControls.coverCreditCardFees;
    return compact([
        effectivePaymentMethod && invoiceModel.isPending(invoice) && {
            id: 'due',
            text: effectivePaymentMethod.last4 === '' ? texts.dueTextPendingDetails({
                date: invoiceModel.paymentProcessingDate(invoice)
            }) : texts.dueText({
                date: invoiceModel.paymentProcessingDate(invoice),
                type: effectivePaymentMethod.type,
                last4: effectivePaymentMethod.last4
            })
        },
        coversCreditCardFees === false && (isAdHoc || (paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.type) === 'creditCard') && {
            id: 'cc',
            text: texts.creditCardFees()
        }
    ]);
}
export function getURLWithFullPageInvoice(config) {
    const { invoiceId, relationshipId, pathname, search } = config;
    const url = new URL(pathname || location.pathname, location.origin);
    const searchParams = new URLSearchParams(search || location.search);
    searchParams.forEach((value, key)=>url.searchParams.append(key, value));
    url.searchParams.set(invoiceSearchParam, invoiceId);
    url.searchParams.set(relationshipSearchParam, relationshipId);
    return url.pathname + url.search;
}
function syncRefundCallout(param, texts) {
    let { accounting, syncRefundAction } = param;
    return {
        id: 'sync-refund',
        intent: 'info',
        text: texts.refundReadyToSync({
            accountingName: getAccountingDetails(accounting).fullName
        }),
        status: 'pending',
        actions: [
            syncRefundAction
        ]
    };
}
function isItemLinked(item, serviceTemplates, accounting) {
    return getAccountingDetails(accounting).policy.checkServiceMapping({
        item,
        serviceTemplates,
        accounting
    });
}
const syncAfterFailureCallout = (param)=>{
    let { items, contacts, accounting, integration, relationship, retrySyncAction, serviceTemplates, mapContactsAction, mapServicesAction, mapTaxRatesAction, accountingSyncErrorTexts, retrySyncWithoutTimeActivitiesAction } = param;
    return (data)=>{
        const isClientContactLinked = getAccountingDetails(accounting).policy.checkContactsMapping({
            contacts,
            relationship,
            accounting
        });
        const allServicesLinked = items.every((item)=>{
            return isItemLinked(item, serviceTemplates, accounting);
        });
        const failureCode = data.failure.failureCode();
        const failedToMapServices = !allServicesLinked && [
            AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodeInvoiceServiceNotMapped,
            AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodeInvoiceClientAndServiceNotMapped
        ].includes(failureCode);
        const failedToMapContacts = !isClientContactLinked && [
            AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodeInvoiceClientNotMapped,
            AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodeInvoiceClientAndServiceNotMapped
        ].includes(failureCode);
        const failedToMapTimeActivities = AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrTimeActivityCannotAppliedToInvoice === failureCode;
        const failedToMapTaxRates = AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodeInvoiceTaxNotMapped === failureCode;
        const mappingActions = compact([
            failedToMapContacts && mapContactsAction,
            failedToMapServices && mapServicesAction,
            failedToMapTaxRates && mapTaxRatesAction,
            ...failedToMapTimeActivities ? [
                retrySyncWithoutTimeActivitiesAction,
                retrySyncAction
            ] : []
        ]);
        return {
            id: 'sync-after-failure',
            intent: 'warning',
            text: getAccountingInvoiceErrorMessage({
                invoiceIntegration: integration,
                accounting,
                texts: accountingSyncErrorTexts
            }),
            status: 'error',
            actions: mappingActions.length > 0 ? mappingActions : [
                retrySyncAction
            ]
        };
    };
};
export function getAccountingSectionCallout(params, texts) {
    const { accounting, integration, invoiceStatus, numRefunds, isZeroAmount, syncAction, syncPaymentAction } = params;
    if (!integration) return null;
    return integration.accept({
        whenPendingSync: ()=>({
                id: 'ready-to-sync',
                intent: 'info',
                text: texts.invoiceReadyToSync({
                    accountingName: getAccountingDetails(accounting).fullName
                }),
                status: 'pending',
                actions: [
                    syncAction
                ]
            }),
        whenRefundSyncFailed: syncAfterFailureCallout(_object_spread_props(_object_spread({}, params), {
            integration
        })),
        whenInvoiceSyncFailed: syncAfterFailureCallout(_object_spread_props(_object_spread({}, params), {
            integration
        })),
        whenPaymentSyncFailed: syncAfterFailureCallout(_object_spread_props(_object_spread({}, params), {
            integration
        })),
        whenInvoiceRecorded: ()=>{
            if ([
                InvoiceStatus.PaymentConfirmed,
                InvoiceStatus.Paid
            ].includes(invoiceStatus) && !isZeroAmount) return {
                id: 'payment-sync-failed',
                intent: 'info',
                text: texts.paymentReadyToSync({
                    accountingName: getAccountingDetails(accounting).fullName
                }),
                status: 'pending',
                actions: [
                    syncPaymentAction
                ]
            };
            return null;
        },
        whenVoidedRecorded: ()=>null,
        whenPaymentRecorded: ()=>{
            if (invoiceStatus === InvoiceStatus.Refunded || invoiceStatus === InvoiceStatus.PartiallyRefunded) return syncRefundCallout(params, texts);
            return null;
        },
        whenRefundRecorded: (refundRecorded)=>{
            if (refundRecorded.numRefunds != null && numRefunds != null && refundRecorded.numRefunds < numRefunds) return syncRefundCallout(params, texts);
            return null;
        },
        whenReturnRecorded: ()=>null
    });
}
export function makeResumePaymentInvoiceAction(isTerminatedRelationship, texts) {
    return {
        text: texts.resumePayment(),
        intent: Intent.InvoiceResumePayment,
        disabled: isTerminatedRelationship,
        tooltipTitle: isTerminatedRelationship ? texts.terminated() : undefined
    };
}
export const getGenericAccountingTooltip = (accounting, itemName, texts)=>/*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            texts.lineItemMappedTo({
                accountingName: getAccountingDetails(accounting).fullName
            }),
            " ",
            /*#__PURE__*/ _jsx("b", {
                children: itemName
            })
        ]
    });
export const getNotVisibleToClientTooltip = (texts)=>texts.notVisibleToClient();
export const getXeroTooltip = (param, texts)=>{
    let { accounting, itemName, testId } = param;
    return /*#__PURE__*/ _jsxs(Box, {
        style: {
            display: 'block'
        },
        testId: testId,
        children: [
            getGenericAccountingTooltip(accounting, itemName, texts),
            ". ",
            getNotVisibleToClientTooltip(texts)
        ]
    });
};
export const getItemQBOTooltip = (qbo, testId, texts)=>/*#__PURE__*/ _jsxs(Box, {
        style: {
            display: 'block'
        },
        testId: testId,
        children: [
            getGenericAccountingTooltip(Accounting.QBO, qbo.item.name, texts),
            qbo.class && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    ' ',
                    texts.withTheClass(),
                    " ",
                    /*#__PURE__*/ _jsx("b", {
                        children: qbo.class.name
                    })
                ]
            }),
            ". ",
            getNotVisibleToClientTooltip(texts)
        ]
    });
export function onRequestRefundSuccess(param) {
    let { now, reason, invoice, sellerId, onRefresh, setInvoice } = param;
    if (invoiceModel.isPaid(invoice) || invoiceModel.isConfirmed(invoice)) {
        const timestamp = now.getTime();
        setInvoice(invoiceModel.setRefunded(invoice, timestamp, now));
        onRefresh === null || onRefresh === void 0 ? void 0 : onRefresh({
            optimisticUpdate: {
                type: InvoiceOptimisticUpdateType.Refunded,
                invoiceId: invoice.id,
                timestamp
            }
        });
    } else {
        setInvoice(invoiceModel.setRefundRequested(invoice));
        onRefresh === null || onRefresh === void 0 ? void 0 : onRefresh({
            optimisticUpdate: {
                type: InvoiceOptimisticUpdateType.RefundRequested,
                invoiceId: invoice.id,
                userId: sellerId,
                reason
            }
        });
    }
}
export function onRequestPaymentConfirmed(param) {
    let { invoice, isVendor, onRefresh, setInvoice } = param;
    setInvoice(invoiceModel.setPaymentConfirmed(invoice, isVendor));
    onRefresh === null || onRefresh === void 0 ? void 0 : onRefresh({
        optimisticUpdate: {
            type: InvoiceOptimisticUpdateType.PaymentConfirmed,
            invoiceId: invoice.id,
            isVendor
        }
    });
}
export function showRequestPaymentMethodModal(invoice, relationship) {
    if (relationshipModel.isTerminated(relationship)) return false;
    const status = invoiceModel.displayStatus(invoice);
    return invoiceModel.isPaymentMethodUnset(invoice) && (status === InvoiceDisplayStatusV2.Overdue || status === InvoiceDisplayStatusV2.Issued);
}
export function getBadgeSkin(status) {
    switch(status){
        case InvoiceDisplayStatusV2.Issued:
        case InvoiceDisplayStatusV2.Pending:
        case InvoiceDisplayStatusV2.Voided:
        case InvoiceDisplayStatusV2.Refunded:
        case InvoiceDisplayStatusV2.PartiallyRefunded:
            return 'default';
        case InvoiceDisplayStatusV2.Processing:
        case InvoiceDisplayStatusV2.Paid:
        case InvoiceDisplayStatusV2.DisputeWon:
            return 'success';
        case InvoiceDisplayStatusV2.Overdue:
        case InvoiceDisplayStatusV2.RefundFailed:
        case InvoiceDisplayStatusV2.Disputed:
        case InvoiceDisplayStatusV2.LateReturn:
            return 'error';
    }
}
export const getServiceTemplateIds = (invoice)=>{
    return compact(invoice.items.map((item)=>item.serviceTemplateId));
};
export const MobileDivider = ()=>/*#__PURE__*/ _jsx(Divider, {
        margin: 16
    });
export function unverifiedPaymentMethodCallout(param) {
    let { paymentMethod, bankAccountPanelTexts, fullPageTexts } = param;
    if (paymentMethod == null || !paymentMethodModel.isPendingVerification(paymentMethod)) {
        return {
            intent: 'warning',
            text: fullPageTexts.paymentMethodUnverified()
        };
    }
    return {
        intent: 'info',
        text: paymentMethodModel.isPendingManualVerification(paymentMethod) ? bankAccountPanelTexts.manualVerificationConfirmDeposits() : bankAccountPanelTexts.autoVerificationInitiated()
    };
}
